import _ from 'underscore';
import { cloneElement, Component, Children } from 'react';
import styled from 'styled-components';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { accessor } from 'react-big-calendar/lib/utils/accessors';
import moment from 'moment';
import Toggle from '../widget/toggle';

const { I18n } = window;

// Make all available localization options use Monday as the first day of the week
_.each(APP_CONSTANTS.LOCALE_TO_LANGUAGE_CODE_MAP, (languageCode, _localeCode) => {
  moment.updateLocale(languageCode, {
    week: {
      dow: 1,
      doy: 1,
    },
  });
});

const currentTZ = moment.tz.guess();
const calendarLocalizer = momentLocalizer(moment);

const NEW_EVENT_PLACEHOLDER_ID = 'new-event-placeholder';

export const convertDateTimeToDate = (datetime, timeZoneName = currentTZ, dayOnly = false) => {
  const m = moment.tz(datetime, timeZoneName);

  return new Date(m.year(), m.month(), m.date(), dayOnly ? 0 : m.hours(), dayOnly ? 0 : m.minutes(), 0, 0);
};

export const convertDateToDateTime = (date, timeZoneName = currentTZ, dayOnly = false) => {
  const dateM = moment.tz(date, timeZoneName);

  return moment.tz(
    {
      year: dateM.year(),
      month: dateM.month(),
      date: dateM.date(),
      hours: dayOnly ? 0 : dateM.hours(),
      minutes: dayOnly ? 0 : dateM.minutes(),
      seconds: 0,
      milliseconds: 0,
    },
    timeZoneName,
  );
};

const MIN_HOUR = 7;
const MAX_HOUR = 19;

export default class DragAndDropEventCalendar extends Component {
  TRANSLATED_SHOW_EXTENDED_HOURS = I18n.t('js.admin.drag_and_drop_event_calendar.show_extended_hours');

  TRANSLATED_DATE = I18n.t('js.admin.drag_and_drop_event_calendar.date');

  TRANSLATED_TIME = I18n.t('js.admin.drag_and_drop_event_calendar.time');

  TRANSLATED_EVENT = I18n.t('js.admin.drag_and_drop_event_calendar.event');

  TRANSLATED_ALL_DAY = I18n.t('js.admin.drag_and_drop_event_calendar.all_day');

  TRANSLATED_WEEK = I18n.t('js.admin.drag_and_drop_event_calendar.week');

  TRANSLATED_WORK_WEEK = I18n.t('js.admin.drag_and_drop_event_calendar.work_week');

  TRANSLATED_DAY = I18n.t('js.admin.drag_and_drop_event_calendar.day');

  TRANSLATED_MONTH = I18n.t('js.admin.drag_and_drop_event_calendar.month');

  TRANSLATED_PREVIOUS = I18n.t('js.admin.drag_and_drop_event_calendar.previous');

  TRANSLATED_NEXT = I18n.t('js.admin.drag_and_drop_event_calendar.next');

  TRANSLATED_YESTERDAY = I18n.t('js.admin.drag_and_drop_event_calendar.yesterday');

  TRANSLATED_TOMORROW = I18n.t('js.admin.drag_and_drop_event_calendar.tomorrow');

  TRANSLATED_TODAY = I18n.t('js.admin.drag_and_drop_event_calendar.today');

  TRANSLATED_AGENDA = I18n.t('js.admin.drag_and_drop_event_calendar.agenda');

  TRANSLATED_NO_EVENTS_IN_RANGE = I18n.t('js.admin.drag_and_drop_event_calendar.no_events_in_range');

  constructor(props) {
    super(props);

    this.state = {
      events: props.events,
      showExtendedHours: props.alwaysShowExtendedHours,
      startDate: props.date,
    };
  }

  static defaultProps = {
    startAccessor: 'start',
    endAccessor: 'end',
  };

  componentDidMount() {
    this.setMinMaxHours();
  }

  componentDidUpdate(prevProps) {
    const currentEvents = this.props.events;
    const prevEvents = prevProps.events;

    const currentEventIds = this.props.events.map((e) => e.id);
    const prevEventIds = prevProps.events.map((e) => e.id);

    // Boolean true if any events were added in this update
    const eventsWereAdded = currentEventIds.some((id) => prevEventIds.indexOf(id) === -1);

    // Boolean true if any events were removed in this update
    const eventsWereRemoved = prevEventIds.some((id) => currentEventIds.indexOf(id) === -1);

    // Zip current events with previous events. Iterate over them to see if any of the keys we're interested in
    // have been changed. Return a boolean indicating that an update has taken place.
    const eventsWereUpdated =
      currentEvents.length === prevEvents.length &&
      _.zip(currentEvents, prevEvents).some((events) =>
        // props.updateKeys is an array of keys to check if there are any differences between props and prevProps
        // if any of them are, then the event has been updated
        this.props.updateKeys.some((k) => events[0][k] !== events[1][k]),
      );

    // If any of the above modifications have happened, update state
    if (eventsWereAdded || eventsWereRemoved || eventsWereUpdated) {
      this.setState({ events: currentEvents });
    }
  }

  setMinMaxHours = () => {
    this.setState({
      minHour: this.getMinHour(),
      maxHour: this.getMaxHour(),
    });
  };

  getMinHour = () => {
    /*
      minHour is the earliest time we display in the calendar, in this case, 7:00:00

      If there are events in the calendar that start earlier than 7:00:00, then use that event's start time
      minus one hour to be the earliest time we display in the calendar.
        Example: if an event starts at 6:00:00, then minHour will be 5:00:00
        This way, the calendar will always display one hours worth of time before the earliest event start time

      If a user has showExtendedHours toggled, then we want the earliest time we display to be 00:00:00
    */
    let minHour = MIN_HOUR;

    _.each(this.state.events, (event) => {
      const eventHour = parseInt(convertDateToDateTime(event.start, this.props.timeZoneName).format('H'));
      if (eventHour - 1 < minHour) minHour = eventHour - 1;
    });

    if (this.state.showExtendedHours || minHour < 0) minHour = 0;

    return new Date(0, 0, 0, minHour, 0, 0);
  };

  getMaxHour = () => {
    /*
      maxHour is the latest time we display in the calendar, in this case, 19:59:59

      If there are events in the calendar that end later than 19:59:59, then use that event's end time
      plus one hour to be the latest time we display in the calendar.
        Example: if an event ends at 20:59:59, then minHour will be 21:59:59
        This means the calendar actually displays two hours worth of time after the latest event end time
        because we want the full hour of the latest time we display visible and selectable.

      If a user has showExtendedHours toggled, then we want the latest time we display to be 11:59:59
    */
    let maxHour = MAX_HOUR;

    _.each(this.state.events, (event) => {
      const eventHour = parseInt(convertDateToDateTime(event.end, this.props.timeZoneName).format('H'));
      if (maxHour < eventHour + 1) maxHour = eventHour + 1;
    });

    if (this.state.showExtendedHours || maxHour > 23) maxHour = 23;

    return new Date(0, 0, 0, maxHour, 59, 59, 59);
  };

  startAccessor = (event) => {
    const start = accessor(event, this.props.startAccessor);

    return convertDateTimeToDate(start, this.props.timeZoneName);
  };

  endAccessor = (event) => {
    const end = accessor(event, this.props.endAccessor);
    return convertDateTimeToDate(end, this.props.timeZoneName);
  };

  getOptimisticDateChangeMoment = (datetimeMoment) =>
    moment.tz(
      {
        year: datetimeMoment.local().year(),
        month: datetimeMoment.local().month(),
        date: datetimeMoment.local().date(),
        hours: datetimeMoment.local().hours(),
        minutes: datetimeMoment.local().minutes(),
        seconds: 0,
        milliseconds: 0,
      },
      this.props.timeZoneName,
    );

  optimisticallyUpdateEvent = (event, startMoment, endMoment) => {
    const updatedEvent = event;
    updatedEvent.start = this.getOptimisticDateChangeMoment(startMoment);
    updatedEvent.end = this.getOptimisticDateChangeMoment(endMoment);

    const updatedEvents = this.state.events;
    const indexToUpdate = _.findIndex(updatedEvents, (existingEvent) => existingEvent.id === updatedEvent.id);
    updatedEvents[indexToUpdate] = updatedEvent;
    this.setState({
      events: updatedEvents,
    });
  };

  optimisticallyCreatePlaceholderEvent = (startMoment, endMoment) => {
    const newEvent = {
      id: NEW_EVENT_PLACEHOLDER_ID,
      end: this.getOptimisticDateChangeMoment(endMoment),
      start: this.getOptimisticDateChangeMoment(startMoment),
      title: 'New Event',
    };

    const updatedEvents = this.state.events;
    updatedEvents.push(newEvent);
    this.setState({
      events: updatedEvents,
    });
  };

  render() {
    const {
      components,
      culture,
      defaultView,
      onEventDrop,
      onEventResize,
      onSelectEvent,
      onSelectSlot,
      step,
      style,
      timeslots,
      timeZoneName,
      views,
    } = this.props;

    const { events, maxHour, minHour, startDate } = this.state;

    let calendarProps = {
      components,
      culture,
      date: convertDateTimeToDate(startDate, timeZoneName, true),
      defaultView,
      endAccessor: this.endAccessor,
      events,
      localizer: calendarLocalizer,
      max: maxHour,
      min: minHour,
      resizable: !this.props.readOnly,
      selectable: true,
      startAccessor: this.startAccessor,
      step: step || 30,
      style: style || {},
      timeslots: timeslots || 2,
      views,
      onNavigate: (date) => {
        this.setState({
          startDate: convertDateTimeToDate(date, timeZoneName, true),
        });
      },
      messages: {
        date: this.TRANSLATED_DATE,
        time: this.TRANSLATED_TIME,
        event: this.TRANSLATED_EVENT,
        allDay: this.TRANSLATED_ALL_DAY,
        week: this.TRANSLATED_WEEK,
        work_week: this.TRANSLATED_WORK_WEEK,
        day: this.TRANSLATED_DAY,
        month: this.TRANSLATED_MONTH,
        previous: this.TRANSLATED_PREVIOUS,
        next: this.TRANSLATED_NEXT,
        yesterday: this.TRANSLATED_YESTERDAY,
        tomorrow: this.TRANSLATED_TOMORROW,
        today: this.TRANSLATED_TODAY,
        agenda: this.TRANSLATED_AGENDA,
        noEventsInRange: this.TRANSLATED_NO_EVENTS_IN_RANGE,
        showMore: (total) => I18n.t('js.admin.drag_and_drop_event_calendar.show_more', { total }),
      },
    };

    const RecolorableEventWrapper = ({ children, event }) => {
      const style = { ...children.props.style };
      if (event && event.color) {
        style.backgroundColor = event.color;
      }
      return cloneElement(Children.only(children), { style });
    };

    if (this.props.readOnly) {
      calendarProps.resizableAccessor = () => false;
      calendarProps.onSelecting = () => false;
      calendarProps.onSelectSlot = () => false;
      calendarProps.onSelectEvent = (event) => {
        this.props.onClickEvent(event.id);
      };
      calendarProps.components = {
        eventWrapper: RecolorableEventWrapper,
      };
    } else {
      calendarProps = {
        ...calendarProps,
        onEventDrop: ({ event, start, end }) => {
          const convertedStart = convertDateToDateTime(start, timeZoneName);
          const convertedEnd = convertDateToDateTime(end, timeZoneName);

          this.optimisticallyUpdateEvent(event, convertedStart, convertedEnd);

          onEventDrop(event, convertedStart, convertedEnd);

          this.setMinMaxHours();
        },
        onEventResize: ({ event, start, end }) => {
          const convertedStart = convertDateToDateTime(start, timeZoneName);
          const convertedEnd = convertDateToDateTime(end, timeZoneName);

          this.optimisticallyUpdateEvent(event, convertedStart, convertedEnd);

          onEventResize(event, convertedStart, convertedEnd);

          this.setMinMaxHours();
        },
        onSelectEvent: (event) => {
          event.start = convertDateToDateTime(event.start, timeZoneName);
          event.end = convertDateToDateTime(event.end, timeZoneName);

          onSelectEvent(event);
        },
        onSelectSlot: ({ start, end }) => {
          const convertedStart = convertDateToDateTime(start, timeZoneName);

          let convertedEnd = convertDateToDateTime(end, timeZoneName);
          if (convertedEnd.diff(convertedStart, 'hours') < 1) {
            convertedEnd = convertDateToDateTime(start, timeZoneName);
            convertedEnd.add(1, 'hour');
          }

          this.optimisticallyCreatePlaceholderEvent(convertedStart, convertedEnd);

          onSelectSlot(convertedStart, convertedEnd, this.deletePlaceholderEvent);
        },
      };
    }

    const useDragAndDrop = !this.props.readOnly;
    const DragAndDropCalendar = useDragAndDrop ? withDragAndDrop(Calendar) : Calendar;

    return (
      <DragAndDropEventCalendarWrapper>
        {!this.props.alwaysShowExtendedHours && (
          <ExtendedHoursToggle>
            {this.TRANSLATED_SHOW_EXTENDED_HOURS}
            <Toggle onChange={this.toggleExtendedHours} active={this.state.showExtendedHours} />
          </ExtendedHoursToggle>
        )}
        <DragAndDropCalendar {...calendarProps} />
      </DragAndDropEventCalendarWrapper>
    );
  }

  toggleExtendedHours = () => {
    this.setState(
      {
        showExtendedHours: !this.state.showExtendedHours,
      },
      () => {
        this.setMinMaxHours();
      },
    );
  };

  deletePlaceholderEvent = () => {
    const updatedEvents = this.state.events;
    const indexToDelete = _.findIndex(updatedEvents, (existingEvent) => existingEvent.id === NEW_EVENT_PLACEHOLDER_ID);

    if (indexToDelete > -1) {
      updatedEvents.splice(indexToDelete, 1);
      this.setState({
        events: updatedEvents,
      });
    }
  };
}

export const DragAndDropEventCalendarWrapper = styled.div`
  .rbc-row-content {
    .rbc-row {
      .rbc-date-cell {
        &.rbc-off-range {
          color: ${({ theme }) => theme.colors.decorative.neutral[600]};
        }
      }
    }
  }

  .rbc-day-bg {
    &.rbc-off-range-bg {
      background-color: ${({ theme }) => theme.colors.decorative.neutral[100]};
    }
  }

  .rbc-event-label {
    font-size: 12px;
    padding-bottom: 5px;
    padding-right: 0;
    order: 2;
  }

  .rbc-event-content {
    font-size: 12px;
    font-weight: bold;
    flex: none;
    overflow: hidden;
    margin-top: 5px;
    height: auto;
    min-height: auto;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rbc-addons-dnd-resizable,
  .rbc-addons-dnd-drag-preview {
    display: flex;
    flex-flow: column;
  }

  .rbc-addons-dnd .rbc-addons-dnd-resize-ns-anchor {
    width: auto;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
  }
`;

const ExtendedHoursToggle = styled.div`
  margin-bottom: 5px;
`;
